import { Controller } from "@hotwired/stimulus";
import * as mapboxgl from 'mapbox-gl';
import { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FeatureCollection, Feature, Point, GeoJsonProperties, LineString } from 'geojson';
import { createMap } from '../../../../scripts/mapbox';
import * as moment from "moment";
import { size } from "lodash";
import { Colors } from "../../../../scripts/_generated/Colors";
import { forceMapToRedrawWhenInitiallyHidden } from "../../../../Components/Map/MapComponentShared";

export default class HeatmapController extends Controller {
    static targets = [
        'container',
        'zoomAlert',
        'zoomValue',
        'dateFrom',
        'dateTill',
        'applyButton',
        'progressIndicator',
        'heatmapOpacity',
        'heatmapWeight',
        'heatmapIntensity',
        'heatmapRadius',
        'normalizeData',
        'deviceLanguage',
        'utmCampaign',
        'poiIcon',
        'showPois',
        'showTours',
    ];
    static values = {
        loadDataUrl: String,
        loadPoisUrl: String,
        loadToursUrl: String,
        initialBoundingBox: Array,
    }

    readonly loadDataUrlValue!: string;
    readonly loadPoisUrlValue!: string;
    readonly loadToursUrlValue!: string;
    readonly initialBoundingBoxValue!: [number, number, number, number];

    readonly containerTarget!: HTMLElement;
    readonly zoomAlertTarget!: HTMLElement;
    readonly zoomValueTarget!: HTMLInputElement;
    readonly dateFromTarget!: HTMLInputElement;
    readonly dateTillTarget!: HTMLInputElement;
    readonly applyButtonTarget!: HTMLButtonElement;
    readonly progressIndicatorTarget!: HTMLElement;
    readonly heatmapOpacityTarget!: HTMLInputElement;
    readonly heatmapWeightTarget!: HTMLTextAreaElement;
    readonly heatmapIntensityTarget!: HTMLTextAreaElement;
    readonly heatmapRadiusTarget!: HTMLTextAreaElement;
    readonly normalizeDataTarget!: HTMLInputElement;
    readonly deviceLanguageTarget!: HTMLSelectElement;
    readonly utmCampaignTarget!: HTMLInputElement;
    readonly poiIconTarget!: HTMLImageElement;
    readonly showPoisTarget!: HTMLInputElement;
    readonly showToursTarget!: HTMLInputElement;

    map!: Map;
    geoData!: FeatureCollection<Point>;
    poiData!: FeatureCollection<Point>;
    tourData!: FeatureCollection<LineString>;
    dateFrom = new Date(2022, 8, 1);
    dateTill = new Date(2022, 8, 31);

    connect() {
        this.geoData = {
            type: 'FeatureCollection',
            features: []
        };
        this.poiData = {
            type: 'FeatureCollection',
            features: []
        };
        this.tourData = {
            type: 'FeatureCollection',
            features: []
        };
        this.applyDateRange();

        this.map = createMap(this.containerTarget);
        this.map.on('load', () => {
            this.initializeMapLayers();
        })
        this.map.on('moveend', () => this.updateData());

        forceMapToRedrawWhenInitiallyHidden(this.containerTarget, this.map, () => this.map.fitBounds(this.initialBoundingBoxValue));
    }

    applyDateRange() {
        if (!this.dateFromTarget.valueAsDate || !this.dateTillTarget.valueAsDate) {
            return;
        }

        if (this.dateFromTarget.valueAsDate > this.dateTillTarget.valueAsDate) {
            this.dateTillTarget.valueAsDate = this.dateFromTarget.valueAsDate;
        }

        this.dateFrom = this.dateFromTarget.valueAsDate;
        this.dateTill = this.dateTillTarget.valueAsDate;
    }

    updateData() {
        const zoomLevel = this.map.getZoom();
        this.zoomValueTarget.value = zoomLevel.toString();
        this.zoomAlertTarget.hidden = zoomLevel >= 7;
        if (zoomLevel < 7) {
            return;
        }

        const mapBounds = this.map.getBounds();
        const loadDataUrl = this.loadDataUrlValue
            .replace("$west", mapBounds.getWest().toString())
            .replace("$south", mapBounds.getSouth().toString())
            .replace("$east", mapBounds.getEast().toString())
            .replace("$north", mapBounds.getNorth().toString())
            .replace("$dateFrom", moment(this.dateFrom).format('YYYY-MM-DD'))
            .replace("$dateTill", moment(this.dateTill).format('YYYY-MM-DD'))
            .replace("$normalizeData", `${this.normalizeDataTarget.checked}`)
            .replace("$deviceLanguage", this.deviceLanguageTarget.value)
            .replace("$utmCampaign", this.utmCampaignTarget.value)
            ;

        this.applyButtonTarget.disabled = true;
        this.progressIndicatorTarget.hidden = false;
        fetch(loadDataUrl)
            .then(r => {
                if (!r.ok) {
                    throw new Error('Failed to load heatmap data');
                }
                return r.json();
            })
            .then((d: Feature<Point, GeoJsonProperties>[]) => {
                this.geoData.features = d;
                (this.map.getSource('userLocations') as mapboxgl.GeoJSONSource).setData(this.geoData);
                this.applyButtonTarget.disabled = false;
                this.progressIndicatorTarget.hidden = true;
            });

        if (zoomLevel >= 12) {

            if (this.showPoisTarget.checked) {
                const loadPoisUrl = this.loadPoisUrlValue
                    .replace("$west", mapBounds.getWest().toString())
                    .replace("$south", mapBounds.getSouth().toString())
                    .replace("$east", mapBounds.getEast().toString())
                    .replace("$north", mapBounds.getNorth().toString())
                    .replace("$dateFrom", moment(this.dateFrom).format('YYYY-MM-DD'))
                    .replace("$dateTill", moment(this.dateTill).format('YYYY-MM-DD'));
                fetch(loadPoisUrl)
                    .then(r => {
                        if (!r.ok) {
                            throw new Error('Failed to load POIs');
                        }
                        return r.json();
                    })
                    .then((d: Feature<Point, GeoJsonProperties>[]) => {
                        this.poiData.features = d;
                        (this.map.getSource('pois') as mapboxgl.GeoJSONSource).setData(this.poiData);
                    });
            } else {
                this.poiData.features = [];
                (this.map.getSource('pois') as mapboxgl.GeoJSONSource).setData(this.poiData);
            }

            if (this.showToursTarget.checked) {
                const loadToursUrl = this.loadToursUrlValue
                    .replace("$west", mapBounds.getWest().toString())
                    .replace("$south", mapBounds.getSouth().toString())
                    .replace("$east", mapBounds.getEast().toString())
                    .replace("$north", mapBounds.getNorth().toString())
                    .replace("$dateFrom", moment(this.dateFrom).format('YYYY-MM-DD'))
                    .replace("$dateTill", moment(this.dateTill).format('YYYY-MM-DD'));
                fetch(loadToursUrl)
                    .then(r => {
                        if (!r.ok) {
                            throw new Error('Failed to load tours');
                        }
                        return r.json();
                    })
                    .then((d: Feature<LineString, GeoJsonProperties>[]) => {
                        this.tourData.features = d;
                        (this.map.getSource('tours') as mapboxgl.GeoJSONSource).setData(this.tourData);
                    });
            } else {
                this.tourData.features = [];
                (this.map.getSource('tours') as mapboxgl.GeoJSONSource).setData(this.tourData);
            }
        }
    }

    setPrague() {
        this.map.fitBounds([14.22444, 49.94190, 14.70679, 50.17743]);
    }

    setCzechia() {
        this.map.fitBounds([12.044004221651278, 48.590382228123474, 18.989041913338554, 50.99257683560418]);
    }

    initializeMapLayers() {
        this.map.addSource('userLocations', {
            type: 'geojson',
            data: this.geoData,
        });
        this.map.addSource('tours', {
            type: 'geojson',
            data: this.tourData,
        });
        this.map.addSource('pois', {
            type: 'geojson',
            data: this.poiData,
        });

        this.map.addLayer(
            {
                'id': 'userLocations-heat',
                'type': 'heatmap',
                'source': 'userLocations',
                // see https://docs.mapbox.com/help/tutorials/make-a-heatmap-with-mapbox-gl-js/ or https://docs.mapbox.com/mapbox-gl-js/example/heatmap-layer/ for custom styling
                'paint': {
                    'heatmap-opacity': 0.8,
                    // Increase the heatmap weight based on frequency and property magnitude
                    'heatmap-weight': [
                        'interpolate',
                        ['exponential', 1],
                        ['get', 'weight'],
                        0,
                        0,
                        10,
                        10
                    ],
                    //'heatmap-intensity': 0.1,
                    'heatmap-intensity': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        7,
                        0.1,
                        15,
                        0.1,
                        20,
                        0.8
                    ],
                    //'heatmap-radius': 15,
                    'heatmap-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        7,
                        10,
                        14,
                        10,
                        20,
                        30
                    ],
                }
            },
            'waterway-label'
        );

        this.map.addLayer(
            {
                'id': 'tours',
                'type': 'line',
                'source': 'tours',
                layout: {
                },
                paint: {
                    'line-color': Colors.smgPink,
                    'line-width': 3,
                }
            });

        this.map.addImage('poi-icon', this.poiIconTarget, { sdf: true });
        this.map.addLayer(
            {
                'id': 'pois',
                'type': 'symbol',
                'source': 'pois',
                layout: {
                    'icon-image': 'poi-icon',
                    'icon-size': 0.8,
                    'icon-allow-overlap': true,
                },
                paint: {
                    'icon-opacity': 0.8,
                    'icon-color': Colors.smgViolet,
                }
            });
    }

    setHeatmapOpacity() {
        this.map.setPaintProperty('userLocations-heat', 'heatmap-opacity', this.heatmapOpacityTarget.valueAsNumber/100);
    }

    setHeatmapWeight() {
        this.map.setPaintProperty('userLocations-heat', 'heatmap-weight', JSON.parse(this.heatmapWeightTarget.value));
    }

    setHeatmapIntensity() {
        this.map.setPaintProperty('userLocations-heat', 'heatmap-intensity', JSON.parse(this.heatmapIntensityTarget.value));
    }

    setHeatmapRadius() {
        this.map.setPaintProperty('userLocations-heat', 'heatmap-radius', JSON.parse(this.heatmapRadiusTarget.value));
    }
}
